import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

// Use FontAwesome icons since they are installed in your package.json
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowLeft, 
  faUser, 
  faChevronDown, 
  faChevronUp, 
  faCheck 
} from "@fortawesome/free-solid-svg-icons";

import "./ChildConsentManagement.css";
import { useParentContext } from "../context/ParentContext";
import { structuredOptionalConsents } from "../legal/consentStructure";

const PERMITTED_CONSENT_KEYS = [
  "opt_provider_share",
  "opt_share_health_record",
  "opt_bio_audio_feature",
  "opt_train_audio",
  "opt_bio_video_feature",
  "opt_train_video",
];

export default function ChildConsentManagement() {
  const navigate = useNavigate();
  
  // Fetch Context States
  const { 
    parentState, 
    childConsents, 
    checkParentConsentStatus 
  } = useParentContext();

  const childrenList = parentState?.children || [];

  // Local Component State
  const [expandedChildId, setExpandedChildId] = useState(null);
  const [localChecks, setLocalChecks] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Directly utilize the static structure map without translations
  const consentGroups = structuredOptionalConsents;

  const getChildName = (child) => {
    const first = (child.child_first_name || "").trim();
    const last = (child.child_last_name || "").trim();
    return `${first} ${last}`.trim() || "Unknown Child";
  };

  const toggleChildExpansion = (childId) => {
    if (expandedChildId === childId) {
      setExpandedChildId(null);
    } else {
      // Hydrate local checkbox state from global context
      const activeConsents = childConsents[String(childId)] || [];
      const initialChecks = {};
      
      PERMITTED_CONSENT_KEYS.forEach((key) => {
        initialChecks[key] = activeConsents.includes(key);
      });
      
      setLocalChecks(initialChecks);
      setExpandedChildId(childId);
    }
  };

  const toggleCheck = (id) => {
    if (isSubmitting) return;
    setLocalChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmitConsents = async (childId) => {
    if (!parentState?.parent_id || !childId) return;

    setIsSubmitting(true);
    
    // Calculate Delta against the actual live store
    const activeChildScopes = childConsents[String(childId)] || [];
    const addedConsents = [];
    const revokedConsents = [];

    PERMITTED_CONSENT_KEYS.forEach((key) => {
      const wasChecked = activeChildScopes.includes(key);
      const isCheckedNow = !!localChecks[key];

      if (isCheckedNow && !wasChecked) {
        addedConsents.push(key);
      } else if (!isCheckedNow && wasChecked) {
        revokedConsents.push(key);
      }
    });

    const token = localStorage.getItem("s2stoken");

    try {
      // 1. Commit the delta mutations via API
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/change_child_consents`,
        {
          childId: childId,
          addedConsents: addedConsents,
          revokedConsents: revokedConsents,
          applyAllChild: false, // Ensure changes strictly apply to the individual child
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data?.success) {
        // 2. Re-verify compliance and instantly sync Context global store
        await checkParentConsentStatus(parentState.parent_id);
        
        toast.success("Permissions updated successfully.");
        setExpandedChildId(null); // Collapse panel on success
      } else {
        throw new Error(response.data?.message || "Failed to save consents.");
      }
    } catch (error) {
      console.error("Consent update error:", error);
      toast.error("Could not complete your consent request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="consent-mgmt-wrapper">
      <header className="consent-mgmt-header">
        <button onClick={() => navigate(-1)} className="consent-mgmt-back-btn" aria-label="Back">
          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: "24px", color: "#0F3B4A" }} />
        </button>
        <h1 className="consent-mgmt-title">Manage Permissions</h1>
      </header>

      <p className="consent-mgmt-subtitle">
        Review and update privacy and tracking permissions for your individual children.
      </p>

      {childrenList.map((child) => {
        const isExpanded = expandedChildId === child.child_id;

        return (
          <div key={child.child_id} className="child-accordion-card">
            {/* Accordion Header */}
            <button
              className={`child-accordion-header ${isExpanded ? "expanded" : ""}`}
              onClick={() => toggleChildExpansion(child.child_id)}
            >
              <div className="child-header-left">
                <div className="child-avatar-disc">
                  <FontAwesomeIcon icon={faUser} style={{ fontSize: "16px", color: "#0E7B8A" }} />
                </div>
                <span className="child-name-text">{getChildName(child)}</span>
              </div>
              <FontAwesomeIcon 
                icon={isExpanded ? faChevronUp : faChevronDown} 
                style={{ fontSize: "18px", color: "#7A9AA3" }} 
              />
            </button>

            {/* Accordion Body */}
            {isExpanded && (
              <div className="child-accordion-body">
                {consentGroups.map((group, groupIdx) => {
                  // Filter items to strictly show the permitted 6 targets
                  const validItems = group.items.filter((item) =>
                    PERMITTED_CONSENT_KEYS.includes(item.id)
                  );

                  if (validItems.length === 0) return null;

                  return (
                    <div key={`group-${groupIdx}`} className="consent-group-block">
                      <h3 className="consent-group-heading">{group.heading}</h3>

                      {validItems.map((item) => {
                        const isChecked = !!localChecks[item.id];
                        const isRequired = item.id === "opt_provider_share"; 

                        return (
                          <button
                            key={item.id}
                            className={`consent-row-btn ${isChecked ? "checked" : ""}`}
                            onClick={() => toggleCheck(item.id)}
                            disabled={isSubmitting}
                            type="button"
                          >
                            <div className={`consent-custom-checkbox ${isChecked ? "checked" : ""}`}>
                              {isChecked && <FontAwesomeIcon icon={faCheck} style={{ fontSize: "14px", color: "#FFF" }} />}
                            </div>
                            
                            <div className="consent-content-wrapper">
                              <div className="consent-title-badge-row">
                                <span className="consent-item-title">{item.title}</span>
                                {item.tag && (
                                  <span className={`consent-badge-pill ${isRequired ? "req" : "opt"}`}>
                                    {item.tag}
                                  </span>
                                )}
                              </div>
                              {item.desc && <span className="consent-item-desc">{item.desc}</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Submit Action Block */}
                <div className="consent-submit-wrapper">
                  <button
                    className="consent-submit-btn"
                    onClick={() => handleSubmitConsents(child.child_id)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="consent-spinner"></div>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}